// Standard centrifugal pump correlations (Gülich / Pfleiderer).
// When the customer's Fortran routine is translated to Python, replace predesign()
// with a POST to /api/blades/hydraulic-layout/calculate. The result shape stays the same.

export function interpolateCurve(families, familyName, variant, nq) {
  const family = families.find(f => f.name === familyName);
  if (!family) return null;
  const pts = family.curves?.[variant]?.points;
  if (!pts || pts.length < 2) return null;
  const sorted = [...pts].sort((a, b) => a.x - b.x);
  const xs = sorted.map(p => p.x);
  const ys = sorted.map(p => p.y);
  if (nq <= xs[0])              return ys[0];
  if (nq >= xs[xs.length - 1]) return ys[ys.length - 1];
  for (let i = 0; i < xs.length - 1; i++) {
    if (xs[i] <= nq && nq <= xs[i + 1]) {
      const t = (nq - xs[i]) / (xs[i + 1] - xs[i]);
      return ys[i] + t * (ys[i + 1] - ys[i]);
    }
  }
  return null;
}

export const MACHINE_TYPES = [
  { value: "radial",     label: "Centrifugal – Radial",     nqRange: [10,  55] },
  { value: "mixed_flow", label: "Centrifugal – Mixed Flow", nqRange: [40, 120] },
];

// Conversion factor: nq [SI: rpm, m³/s, m] → ns [US: rpm, GPM, ft]
export const NQ_TO_NS = 51.64;

export function predesign({ n, Q, H, n_stages = 1, machineType = "radial",
                            // stat-curve overrides (null = fall back to Gülich)
                            psi2_stat  = null,
                            phi1_stat  = null,
                            b2_d2_stat = null,
                            beta2_stat = null,
                            z_d2_stat  = null,
                            // legacy manual override kept for backward compat
                            psi = null, phi = null,
                            eta_vol = 0.97, eta_hyd = 0.92 }) {
  const g = 9.81;
  const stages = Math.max(1, Math.round(n_stages));
  const H_stage = H / stages;
  const nq       = n * Math.sqrt(Q) / Math.pow(H_stage, 0.75);
  const nq_total = n * Math.sqrt(Q) / Math.pow(H, 0.75);

  // ψ₂ — stat curve → manual → Gülich estimate
  const psi_est  = machineType === "mixed_flow"
    ? Math.max(0.60, 0.95 - 0.003 * (nq - 40))
    : Math.max(0.75, 1.10 - 0.004 * (nq - 10));
  const psi_used = psi2_stat ?? psi ?? psi_est;

  const omega = 2 * Math.PI * n / 60;   // angular velocity [rad/s]

  const u2  = Math.sqrt(2 * g * H_stage / psi_used);
  const D2  = 60 * u2 / (Math.PI * n);

  // b₂ — from stat curve ratio, else Gülich estimate
  const b2_d2  = b2_d2_stat ?? (0.017 + 0.262 * Math.pow(nq / 100, 0.6));
  const b2     = b2_d2 * D2;

  // Impeller height z — from stat curve ratio, else null
  const z_d2 = z_d2_stat ?? null;
  const z    = z_d2 != null ? z_d2 * D2 : null;

  // β₂ — stat curve → Euler equation
  const cm2   = Q / (Math.PI * D2 * b2);
  const cu2   = g * H / (u2 * eta_hyd);
  const beta2 = beta2_stat ?? (Math.atan2(cm2, u2 - cu2) * 180 / Math.PI);

  // D₁ and φ₁ — formula: φ₁ = 8Q / (D₁³ · ω · π)
  // Inverse:  D₁ = (8Q / (ω · π · φ₁))^(1/3)
  // When stat curve φ₁ is available, derive D₁ from it; otherwise estimate D₁ and compute φ₁.
  let D1, phi1_used;
  if (phi1_stat != null) {
    D1        = Math.cbrt(8 * Q / (omega * Math.PI * phi1_stat));
    phi1_used = phi1_stat;
  } else {
    const ratio = machineType === "mixed_flow"
      ? 0.45 + 0.002 * nq
      : 0.35 + 0.0022 * nq;
    D1        = Math.min(ratio * D2, 0.92 * D2);
    phi1_used = 8 * Q / (Math.pow(D1, 3) * omega * Math.PI);
  }

  const u1        = Math.PI * D1 * n / 60;
  const cm1_inlet = Q / (eta_vol * Math.PI / 4 * D1 * D1 * 0.85);
  const beta1     = Math.atan2(cm1_inlet, u1) * 180 / Math.PI;

  const mt   = MACHINE_TYPES.find(m => m.value === machineType);
  const warn = mt && (nq < mt.nqRange[0] || nq > mt.nqRange[1])
    ? `nq = ${nq.toFixed(1)} is outside the typical range for ${mt.label} (${mt.nqRange[0]}–${mt.nqRange[1]})`
    : null;

  const source = (psi2_stat != null || phi1_stat != null) ? "stat" : "cordier";

  return {
    nq, nq_total, n_stages: stages,
    psi: psi_used, phi: phi1_used,
    u2, cm2, cu2,
    D2_mm: D2 * 1000,
    b2_mm: b2 * 1000,
    b2_d2,
    z_mm:  z != null ? z * 1000 : null,
    D1_mm: D1 * 1000,
    beta1, beta2,
    cm1: cm1_inlet, u1,
    source, warn,
  };
}
